import { useState, useCallback, useEffect, DependencyList, useRef } from 'react';
import { UseClosestAttributeReturn } from './types';

/**
 * `useClosestAttribute` returns the value of the closest attribute from a recursive search through
 * an element's predecessors (ie. any parent element up to the root).
 *
 * @returns A tuple with a callback ref and the current value of the closest attribute.
 *
 * @example
 * ```jsx
 * function NewComponent() {
 *   const isRTL = useIsRTL();
 *   const [value, setRef] = useClosestAttribute('dir', [isRTL]);
 *   return <p ref={setRef}>{value}</p>;
 * }
 * ```
 */
export const useClosestAttribute = function (
  attribute: string,
  deps: DependencyList = []
): UseClosestAttributeReturn {
  const [value, setValue] = useState('');
  const ref = useRef<Element | null>(null);

  const updateValue = useCallback(() => {
    if (ref.current !== null && !!attribute) {
      setValue(ref.current.closest(`[${attribute}]`)?.getAttribute(attribute) ?? '');
    } else {
      setValue('');
    }
  }, [attribute]);

  const setRef = useCallback(
    (element: Element | null) => {
      ref.current = element;
      updateValue();
    },
    [updateValue]
  );

  useEffect(() => {
    updateValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateValue, ...deps]);

  return [value, setRef];
};
